<template>
  <qd-collapse
    class="card"
    animation="slide">
    <template #trigger="props">
      <div class="card-header header-card header-recap-affiche"
        role="button"
        aria-controls="contentIdForA11y3">
        <p class="card-header-title">
          {{ $t('editionCommande.logistiqueImpression.recapitulatif.title')}}
        </p>
        <a class="card-header-icon">
          <b-icon :icon="props.open ? 'menu-down' : 'menu-up'"></b-icon>
        </a>
      </div>
    </template>
    <div class="card-content">
      <v-client-table
        :data="recaBesoins"
        :columns="columns"
        :options="options"
        class="displayNoneVuePagination">
        <template #natureSupportAfficheId="{ row }">
          {{ getLibelleNatureSupportAffiche(row.natureSupportAfficheId) }}
        </template>
        <template #typeMaterielId="{ row }">
          {{ getLibelleTypeMateriel(row.typeMaterielId) }}
        </template>
        <template #appendBody>
          <table-footer-totaux
            :columns="columns"
            :target="targetTotal"
            :items="recaBesoins"
            :formatage="(val) => val">
          </table-footer-totaux>
        </template>
      </v-client-table>
    </div>
  </qd-collapse>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import * as tableConfig from '@/configuration/clientTableConf';
import TableFooterTotaux from '@/components/general/tableFooterTotaux';
import { PARAMETRES_KEYS } from '@/store/modules/parametres-mutation-type';
import messageErreur from '@/services/general/messageErreur';

const Parametres = createNamespacedHelpers('parametres');

export default {
  name: 'RecapitulatifCard',
  props: {
    besoins: {
      type: Array,
      required: true,
    },
  },
  components: {
    TableFooterTotaux,
  },
  computed: {
    ...Parametres.mapGetters({
      parametre: 'parametre',
    }),
    targetTotal() {
      return ['nbAfficheVendue', 'nbAfficheMaintenance', 'nbAfficheTotal'];
    },
    recaBesoins() {
      const reduce = this.besoins.reduce((acc, val) => {
        const key = [val.produitAchatId, val.formatAfficheId].filter(Boolean).join('-');
        if (!key) return acc;
        if (!acc[key]) {
          acc[key] = {
            data: [val],
            produitAchat: val.produitAchat?.libelle ?? '',
            formatAffiche: val.formatAffiche?.libelle ?? '',
            natureSupportAfficheId: val.natureSupportAfficheId ?? null,
            typeMaterielId: val.typeMaterielId ?? null,
            nbAfficheVendue: val.nbAfficheVendue ?? 0,
            nbAfficheMaintenance: val.nbAfficheMaintenance ?? 0,
            nbAfficheTotal: val.nbAfficheTotal ?? 0,
          };
        } else {
          acc[key].data.push(val);
          acc[key].nbAfficheVendue += val.nbAfficheVendue ?? 0;
          acc[key].nbAfficheMaintenance += val.nbAfficheMaintenance ?? 0;
          acc[key].nbAfficheTotal += val.nbAfficheTotal ?? 0;
        }
        return acc;
      }, {});
      return Object.values(reduce);
    },

    columnsData() {
      return [
        { name: 'produitAchat' },
        { name: 'formatAffiche' },
        { name: 'natureSupportAfficheId' },
        { name: 'typeMaterielId' },
        { name: 'nbAfficheVendue' },
        { name: 'nbAfficheMaintenance' },
        { name: 'nbAfficheTotal' },
      ];
    },
    columns() {
      const columns = this.columnsData.map((column) => column.name);
      return columns;
    },
    options() {
      const texts = cloneDeep(tableConfig.TEXTS);
      texts.count = '';
      const headings = this.columnsData.reduce((acc, column) => {
        const key = `editionCommande.logistiqueImpression.recapitulatif.table.${column.name}`;
        acc[column.name] = this.$te(key) ? this.$t(key) : '';
        return acc;
      }, {});
      return {
        texts,
        headings,
        sortable: [],
        filterable: false,
        sortIcon: tableConfig.SORT_ICON,
        perPageValues: tableConfig.NO_PER_PAGE_VALUE,
        perPage: tableConfig.RESULTS_PER_PAGE,
        cellClasses: {
          nbAfficheVendue: [{ class: 'has-text-right', condition: () => true }],
          nbAfficheMaintenance: [{ class: 'has-text-right', condition: () => true }],
          nbAfficheTotal: [{ class: 'has-text-right', condition: () => true }],
        },
      };
    },
  },
  methods: {
    ...Parametres.mapActions({
      loadParametres: 'loadParametres',
    }),
    getLibelleNatureSupportAffiche(natureSupportAfficheId) {
      return this.parametre(PARAMETRES_KEYS.natureSupportAffiche)
        .find(item => item.id === natureSupportAfficheId)?.libelle || '';
    },
    getLibelleTypeMateriel(typeMaterielId) {
      return this.parametre(PARAMETRES_KEYS.materielType)
        .find(item => item.id === typeMaterielId)?.libelle || '';
    },
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      await this.loadParametres([PARAMETRES_KEYS.natureSupportAffiche,
        PARAMETRES_KEYS.materielType]);
    } catch (e) {
      messageErreur(this.$i18n);
      throw e;
    } finally {
      loadingComponent.close();
    }
  },
};

</script>
